/**
 * Intent detection for broker and claim status queries.
 * Handles direct keywords, phrase patterns, indirect questions,
 * common typos from voice transcription, and fuzzy matching.
 */

const BROKER_KEYWORDS: string[] = [
  "vermittler", "makler", "ansprechpartner", "berater", "beraterin",
  "betreuer", "betreuerin", "sachbearbeiter", "sachbearbeiterin",
  "kontaktperson", "agent", "agentur",
  "versicherungsberater", "versicherungsberaterin",
  "versicherungsmakler", "versicherungsmaklerin",
  "versicherungsvermittler", "versicherungsvermittlerin",
  "kundenberater", "kundenberaterin",
  "servicemitarbeiter", "servicemitarbeiterin",
  "kundenbetreuer", "kundenbetreuerin",
];

const BROKER_TYPOS: string[] = [
  "berarter", "beerater", "bearter", "braterin",
  "verimttler", "vermitler", "vermittlre",
  "maklre", "makelr", "makkler",
  "ansprechparnter", "ansprechpartne", "anspechpartner",
  "sachbearbiter", "sachbearbieter",
];

const BROKER_PHRASES: string[] = [
  "wer betreut",
  "wer ist zuständig", "wer ist zustaendig", "wer ist fuer mich zustaendig",
  "wer ist für mich zuständig", "wer ist für mich",
  "an wen wende", "an wen kann ich mich wenden", "an wen soll ich mich wenden",
  "wen kann ich anrufen", "wen kann ich kontaktieren", "wen erreiche ich",
  "kontakt zu meinem", "kontakt zu meiner",
  "wie erreiche ich meinen", "wie erreiche ich meine",
  "telefonnummer meines", "telefonnummer von meinem",
  "mein ansprechpartner", "meine ansprechpartnerin",
  "zuständiger berater", "zustaendiger berater",
  "persönlichen kontakt", "persoenlichen kontakt", "persönlicher kontakt",
  "jemand der mir hilft", "jemanden der mir hilft",
  "mit jemandem sprechen", "mit wem spreche ich",
  "brauche hilfe von", "hilfe von meinem",
  "wer kümmert sich", "wer kuemmert sich",
  "kontaktdaten", "kontaktinformation",
  "erreichbar", "erreichbarkeit",
  "wer ist mein berater", "wer ist mein makler", "wer ist mein vermittler",
  "wie heißt mein berater", "wie heisst mein berater",
  "wer ist denn mein", "wer ist eigentlich mein",
  "wer hat mich betreut", "wer betreut mich",
  "kennt ihr meinen berater", "kennen sie meinen berater",
  "weiß nicht an wen", "weiss nicht an wen",
  "nicht an wen ich mich wenden",
];

const CLAIM_STATUS_KEYWORDS: string[] = [
  "schadenstatus", "schadenstand", "schadenmeldung",
  "schadenfall", "schadensfall",
  "schadenbearbeitung", "schadensbearbeitung",
  "schadenregulierung", "schadensregulierung",
  "schadenabwicklung", "schadensabwicklung",
  "schadennummer", "schadensnummer",
  "schadenauskunft", "schadeninfo", "schadeninformation",
  "schadenvorgang", "schadensverlauf",
  "schadenprozess", "schadenverfolgung",
];

const CLAIM_STATUS_TYPOS: string[] = [
  "schadne", "schadne status", "schadnestatus",
  "schadenstaus", "schadenstatus",
  "scahden", "scahdenfall",
  "schadnefall", "schadnestand",
  "stauts", "satus", "statsus",
  "schadn", "schden", "schaden staus",
  "schadenmledung", "schadenmedlung",
];

const CLAIM_STATUS_COMPOUND_WORDS: string[] = [
  "status", "stand", "bearbeitung", "fortschritt",
  "zustand", "verlauf", "update", "neuigkeit", "neuigkeiten",
  "ergebnis", "ausgang", "regulierung", "abwicklung",
  "prozess", "vorgang", "verfolgung", "meldung",
  "bearbeitungsstand", "bearbeitungstand",
];

const CLAIM_STATUS_PHRASES: string[] = [
  "mein schaden", "meinen schaden", "meinem schaden", "meines schadens",
  "der schaden", "den schaden", "dem schaden", "des schadens",
  "mein fall", "meinen fall", "meine meldung", "meiner meldung",
  "wie weit ist", "wie weit ist mein",
  "was macht mein schaden", "was macht der schaden",
  "was macht meine meldung",
  "gibt es neuigkeiten", "gibt es was neues", "gibt es ein update",
  "gibts was neues", "gibts neuigkeiten",
  "wie sieht es aus", "wie sieht es mit meinem",
  "was passiert mit meinem", "was passiert mit dem",
  "wie läuft es", "wie laeuft es", "wie läuft mein",
  "wann wird bearbeitet", "wann wird mein schaden bearbeitet",
  "wie steht es um", "wie steht es mit", "wie stehts",
  "update zu meinem", "update zum schaden",
  "aktueller stand", "aktuellen stand",
  "noch in bearbeitung", "wird noch bearbeitet",
  "habe schaden gemeldet", "hab schaden gemeldet",
  "habe einen schaden gemeldet", "hab einen schaden gemeldet",
  "schaden eingereicht", "schaden gemeldet",
  "warte auf antwort", "warte auf rückmeldung", "warte auf rueckmeldung",
  "warte seit", "warte schon",
  "schaden angezeigt", "schaden angemeldet",
  "rückmeldung schaden", "rueckmeldung schaden",
  "rückmeldung zu meinem", "rueckmeldung zu meinem",
  "was ist mit meinem schaden",
  "was ist der stand", "was ist der status",
  "wie ist der stand", "wie ist der status",
  "wie ist mein schaden", "wie ist mein schadenfall",
  "was los mit meinem schaden", "was is mit meinem schaden",
  "was is los mit meinem", "was ist los mit meinem",
  "wo steht mein schaden", "wo steht der schaden",
  "infos zu meinem schaden", "info zu meinem schaden",
  "information zu meinem schaden", "informationen zu meinem",
  "möchte wissen wie es", "moechte wissen wie es",
  "wissen wie es um meinen schaden",
  "wissen was mit meinem schaden",
  "könnt ihr mir sagen", "können sie mir sagen",
  "erfahren wie mein schaden", "erfahren was mit meinem",
  "schaden nachfragen", "nachfrage schaden",
  "schadenfall nachfragen",
  "regulierung meines",
  "wurde mein schaden", "ist mein schaden",
  "schon bearbeitet", "schon reguliert",
];

function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[.,!?;:'"()[\]{}]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function containsAny(text: string, patterns: string[]): boolean {
  return patterns.some(p => text.includes(p));
}

function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      matrix[i][j] = b[i - 1] === a[j - 1]
        ? matrix[i - 1][j - 1]
        : Math.min(matrix[i - 1][j - 1] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j] + 1);
    }
  }
  return matrix[b.length][a.length];
}

function fuzzyMatchesAnyKeyword(words: string[], keywords: string[], maxDistance: number = 1): boolean {
  return words.some(word =>
    word.length >= 5 && keywords.some(kw => {
      if (Math.abs(word.length - kw.length) > maxDistance) return false;
      return levenshteinDistance(word, kw) <= maxDistance;
    })
  );
}

export function isBrokerQuery(message: string): boolean {
  const lower = message.toLowerCase();
  const normalized = normalizeText(message);
  const words = normalized.split(" ");

  if (containsAny(lower, BROKER_KEYWORDS)) return true;
  if (containsAny(lower, BROKER_TYPOS)) return true;
  if (containsAny(lower, BROKER_PHRASES)) return true;
  if (containsAny(normalized, BROKER_PHRASES.map(p => normalizeText(p)))) return true;
  if (fuzzyMatchesAnyKeyword(words, BROKER_KEYWORDS)) return true;

  return false;
}

export function isClaimStatusQuery(message: string): boolean {
  const lower = message.toLowerCase();
  const normalized = normalizeText(message);
  const words = normalized.split(" ");

  if (containsAny(lower, CLAIM_STATUS_KEYWORDS)) return true;
  if (containsAny(lower, CLAIM_STATUS_TYPOS)) return true;
  if (containsAny(lower, CLAIM_STATUS_PHRASES)) return true;
  if (containsAny(normalized, CLAIM_STATUS_PHRASES.map(p => normalizeText(p)))) return true;

  const hasSchadenWord = lower.includes("schaden") || lower.includes("schadne") || lower.includes("scahden");
  if (hasSchadenWord && containsAny(lower, CLAIM_STATUS_COMPOUND_WORDS)) return true;

  if (fuzzyMatchesAnyKeyword(words, CLAIM_STATUS_KEYWORDS)) return true;

  return false;
}
